/**
 * The wire protocol for live games.
 *
 * Hybrid on purpose: the informational events say what just happened so the UI
 * can react ("Mila took the trick"), and an authoritative `state-update`
 * snapshot follows every change. A client that misses or misreads an event is
 * therefore always corrected by the next snapshot, and reconnecting needs no
 * replay — just ask for the state.
 */

import type { Card, Suit, Trick, TrumpSuit } from './game';
import type { LiveGameState } from './live';
import type { HeartsGameState } from './hearts';
import type { KlaverjasLiveState } from './klaverjasLive';
import type { PassDirection } from '../game-logic/hearts';

export const WS_PATH = '/ws';
export const WS_HEARTBEAT_INTERVAL = 30_000;

// --- client → server -------------------------------------------------------

export interface ClientJoin {
  type: 'join';
  code: string;
  playerName: string;
  /**
   * Who tapped their face on the join screen. A hint, never trusted: the
   * server looks the profile up and ignores anything it does not know.
   */
  profileId?: string | null;
  /** Present when returning to a seat this device already holds. */
  rejoinToken?: string;
}

export interface ClientReconnect {
  type: 'reconnect';
  code: string;
  rejoinToken: string;
}

export interface ClientReady {
  type: 'ready';
  ready: boolean;
}

export interface ClientStartGame {
  type: 'start-game';
}

/** Klaverjassen: name a trump suit, or pass when the rules allow it. */
export interface ClientChooseTrump {
  type: 'choose-trump';
  suit: Suit | null;
}

/** Hartenjagen: the three cards this seat is passing on. */
export interface ClientPassCards {
  type: 'pass-cards';
  cards: Card[];
}

export interface ClientPlaceBid {
  type: 'place-bid';
  bid: number;
}

export interface ClientPlayCard {
  type: 'play-card';
  card: Card;
}

/**
 * Play the only card in hand, without naming it.
 *
 * Needed for the blind round: the player cannot see their own card, so the device
 * has nothing to put in `card`. Kept as its own message rather than making
 * `card` optional, so the exhaustiveness guard in the client reducer keeps
 * working and the distinction lives in the protocol instead of in a runtime
 * surprise.
 */
export interface ClientPlayBlind {
  type: 'play-blind';
}

/**
 * Seat a computer player, or drop the last one. Host only, lobby only.
 *
 * No payload, for the same reason `start-game` has none: a client never names a
 * seat. Which seat appears, and which one goes, is the server's to decide.
 */
/**
 * Klaverjassen: knock for the roem in the trick on the table.
 *
 * No payload, and deliberately no hint beforehand that there is anything to
 * knock for — that is the whole point of the option. The server checks whether
 * your team took the trick and whether there was roem in it.
 */
export interface ClientClaimRoem {
  type: 'claim-roem';
}

export interface ClientAddBot {
  type: 'add-bot';
}

export interface ClientRemoveBot {
  type: 'remove-bot';
}

export interface ClientRequestState {
  type: 'request-state';
}

export type ClientMessage =
  | ClientJoin
  | ClientReconnect
  | ClientReady
  | ClientStartGame
  | ClientPlaceBid
  | ClientPassCards
  | ClientChooseTrump
  | ClientPlayCard
  | ClientPlayBlind
  | ClientClaimRoem
  | ClientAddBot
  | ClientRemoveBot
  | ClientRequestState;

// --- server → client -------------------------------------------------------

export interface ServerJoined {
  type: 'joined';
  seat: number;
  /**
   * Opaque handle to this seat, stored in localStorage. Phones close tabs and
   * background aggressively, so identity must survive that — and it must not be
   * guessable from a player's name.
   */
  rejoinToken: string;
  /** Which game you just sat down at, and its opening state. */
  mode: 'live' | 'hearts' | 'klaverjas';
  state: LiveGameState | HeartsGameState | KlaverjasLiveState;
}

export interface ServerStateUpdate {
  type: 'state-update';
  /**
   * Whichever game this room is. The client knows which from the room it
   * joined; narrowing on `'handNo' in state` is the cheap check where it does
   * not.
   */
  state: LiveGameState | HeartsGameState | KlaverjasLiveState;
}

export interface ServerPlayerJoined {
  type: 'player-joined';
  seat: number;
  name: string;
}

export interface ServerPlayerLeft {
  type: 'player-left';
  seat: number;
}

export interface ServerPlayerReconnected {
  type: 'player-reconnected';
  seat: number;
}

export interface ServerRoundDealt {
  type: 'round-dealt';
  roundNo: number;
  cards: number;
  dealerSeat: number;
  trumpCard: Card | null;
  trumpSuit: TrumpSuit;
}

export interface ServerBidPlaced {
  type: 'bid-placed';
  seat: number;
  bid: number;
}

export interface ServerAllBidsIn {
  type: 'all-bids-in';
  bids: Array<{ seat: number; bid: number }>;
}

export interface ServerCardPlayed {
  type: 'card-played';
  seat: number;
  card: Card;
}

export interface ServerTrickWon {
  type: 'trick-won';
  seat: number;
  trick: Trick;
}

/** Hartenjagen: a hand is dealt and, if this one passes, everyone must choose. */
export interface ServerHandDealt {
  type: 'hand-dealt';
  handNo: number;
  direction: PassDirection;
}

/** Hartenjagen: the exchange happened; look at your hand again. */
export interface ServerCardsExchanged {
  type: 'cards-exchanged';
  direction: PassDirection;
}

/** Klaverjassen: a fresh deal, and who has to decide on trump. */
export interface ServerDealDealt {
  type: 'deal-dealt';
  dealNo: number;
  dealerSeat: number;
  chooserSeat: number;
}

export interface ServerTrumpChosen {
  type: 'trump-chosen';
  seat: number;
  suit: Suit;
  playingTeam: 0 | 1;
}

export interface ServerTrumpPassed {
  type: 'trump-passed';
  seat: number;
  nextSeat: number;
}

export interface ServerDealScored {
  type: 'deal-scored';
  dealNo: number;
  points: [number, number];
  totals: [number, number];
  nat: boolean;
  pitTeam: 0 | 1 | null;
}

export interface ServerHandScored {
  type: 'hand-scored';
  handNo: number;
  points: number[];
  totals: number[];
  /** Who took all twenty-six, if anyone. Worth its own announcement. */
  moonSeat: number | null;
}

export interface ServerRoundScored {
  type: 'round-scored';
  roundNo: number;
  entries: Array<{ seat: number; bid: number; tricksWon: number; points: number }>;
  totals: Array<{ seat: number; total: number }>;
}

export interface ServerGameEnded {
  type: 'game-ended';
  standings: Array<{ seat: number; name: string; total: number; placement: number }>;
}

export type ServerErrorCode =
  | 'ROOM_NOT_FOUND'
  | 'ROOM_FULL'
  | 'ALREADY_STARTED'
  | 'NAME_TAKEN'
  | 'NOT_YOUR_TURN'
  | 'INVALID_PHASE'
  | 'ILLEGAL_BID'
  | 'ILLEGAL_CARD'
  /** Hartenjagen: the three cards being passed are not three cards you hold. */
  | 'ILLEGAL_PASS'
  /** Klaverjassen: you knocked for roem, and there was none of yours to take. */
  | 'NO_ROEM'
  | 'NOT_HOST'
  | 'NOT_ENOUGH_PLAYERS'
  | 'NOT_READY'
  | 'INVALID_REQUEST'
  | 'UNKNOWN_SESSION';

export interface ServerError {
  type: 'error';
  code: ServerErrorCode;
  message: string;
}

/** Somebody knocked, and it was there. */
export interface ServerRoemClaimed {
  type: 'roem-claimed';
  seat: number;
  team: 0 | 1;
  points: number;
}

export type ServerMessage =
  | ServerRoemClaimed
  | ServerJoined
  | ServerStateUpdate
  | ServerPlayerJoined
  | ServerPlayerLeft
  | ServerPlayerReconnected
  | ServerRoundDealt
  | ServerBidPlaced
  | ServerAllBidsIn
  | ServerCardPlayed
  | ServerTrickWon
  | ServerRoundScored
  | ServerHandDealt
  | ServerCardsExchanged
  | ServerHandScored
  | ServerDealDealt
  | ServerTrumpChosen
  | ServerTrumpPassed
  | ServerDealScored
  | ServerGameEnded
  | ServerError;

/** Loose guard: the server validates the payload properly per message type. */
export function isClientMessage(value: unknown): value is ClientMessage {
  return typeof value === 'object' && value !== null && 'type' in value;
}
